import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowDownRight,
  ArrowLeft,
  ArrowUpRight,
  BadgeCheck,
  BarChart3,
  Check,
  CheckCircle2,
  Clock,
  Copy,
  Gauge,
  Globe,
  Info,
  ListFilter,
  Loader2,
  Lock,
  Minus,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  TriangleAlert,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import type { EmailSender } from '@blogforge/shared';
import { api } from '../hooks/api.js';
import { useAuthStore } from '../stores/authStore.js';
import { PaneLoading } from '../components/PaneLoading.js';
import { formatDate } from '../lib/billing-format.js';
import {
  advanceDomainVerification,
  advanceMailHealth,
  buildDomainVerification,
  buildMailHealth,
  isPublicMailboxDomain,
  senderData,
  senderDomain,
  senderFromAddress,
  senderRelay,
  type DnsCheck,
  type DnsCheckStatus,
  type DnsRecord,
  type DomainVerification,
  type MailHealth,
} from '../lib/sender-domain.js';
import {
  METRIC_THRESHOLDS,
  TONE_TEXT,
  buildSenderMetrics,
  deltaPercent,
  formatCount,
  formatRate,
  rateOf,
  rateTone,
  type DailyVolume,
  type EnrichedSenderMetrics,
  type MetricTone,
  type MetricTotals,
} from '../lib/sender-metrics.js';

// ─────────────────────────────────────────────────────────────────
// One sender, in full.
//
// The Senders list answers "which identities can we send as"; this page
// answers the two questions that decide whether mail from one of them will
// actually land:
//
//   1. Can BlogForger prove this workspace manages the domain? That is a
//      single TXT record for a domain the customer owns — the Domain tab
//      carries the record, the retry and the verdict.
//   2. Are the domain's mail records sound? SPF, DKIM and DMARC (plus MX,
//      MTA-STS and the relay's own reputation for a shared mailbox host) are
//      read from public DNS and reported as findings with the exact record to
//      add or mend.
//
// A shared consumer mailbox — gmail.com, outlook.com — cannot be verified and
// cannot be edited: its zone belongs to Google or Microsoft. Those senders get
// the Mail health tab instead of the Domain tab, which is the same DNS report
// framed as "what a receiver sees", with the fixes that are actually the
// customer's to make spelled out and the ones that are not said plainly.
//
// The metrics, the DNS answers and the check results on this page are SAMPLE
// DATA (see lib/sender-domain.ts and lib/sender-metrics.ts) — there is no
// resolver or per-sender reporting behind it yet. Both tabs say so where a
// reader would otherwise assume the numbers came from a live lookup.
// ─────────────────────────────────────────────────────────────────

type TabId = 'overview' | 'domain' | 'health';

interface TabDef {
  id: TabId;
  label: string;
  icon: LucideIcon;
  /** Badge count, or null for none: blockers on Domain, fixes on Mail health. */
  count: number | null;
}

// ─────────────────────────────────────────────────────────────────
// Small formatting helpers
// ─────────────────────────────────────────────────────────────────

function formatDay(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function formatTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** The value as a DNS panel wants it pasted, quoted when it contains spaces. */
function dnsValue(value: string): string {
  return /\s/.test(value) ? `"${value}"` : value;
}

/** One record as the line a user can paste into a ticket or a zone file. */
export function recordLine(record: DnsRecord): string {
  return `${record.type.padEnd(5)} ${record.fqdn}  ${dnsValue(record.value)}`;
}

/** A set of records as one copyable block. */
export function recordsText(records: readonly DnsRecord[]): string {
  return records.map(recordLine).join('\n');
}

/** Everything the user has to add, as one copyable block. */
export function pendingRecordsText(checks: readonly DnsCheck[]): string {
  return recordsText(recordsToAdd(checks));
}

/** Records that need to be created — what "x records to add" counts. */
export function recordsToAdd(checks: readonly DnsCheck[]): DnsRecord[] {
  return checks
    .filter((check) => check.status === 'action' && check.expected !== null)
    .map((check) => check.expected as DnsRecord);
}

// ─────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────

export function SenderViewPage() {
  const { id } = useParams<{ id: string }>();
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);

  const senderQuery = useQuery({
    queryKey: ['email-senders', 'detail', id, activeWorkspaceId],
    queryFn: () => api.emailSenders.get(id as string),
    enabled: Boolean(id && activeWorkspaceId),
  });

  const senderMetricsQuery = useQuery({
    queryKey: ['email-senders', 'metrics', id, activeWorkspaceId],
    queryFn: () => api.emailSenders.getMetrics(id as string),
    enabled: Boolean(id && activeWorkspaceId),
  });

  const sender = senderQuery.data ?? null;
  const metrics = senderMetricsQuery.data ?? null;

  /**
   * The two reports, derived from the sender. Everything the page shows about
   * DNS hangs off these, so they are built once per sender rather than per
   * render — the seeded sample data is stable, but the dates are not.
   */
  const report = useMemo(
    () =>
      sender
        ? { verification: buildDomainVerification(sender), health: buildMailHealth(sender) }
        : null,
    [sender],
  );

  // A "re-check" walks the report one step forward locally (there is no
  // resolver yet), so the fresh copy is kept beside the derived one and thrown
  // away when the sender changes.
  const [verified, setVerified] = useState<DomainVerification | null>(null);
  const [rechecked, setRechecked] = useState<MailHealth | null>(null);
  const [checking, setChecking] = useState<TabId | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    setVerified(null);
    setRechecked(null);
    setChecking(null);
  }, [id]);

  useEffect(
    () => () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    },
    [],
  );

  const verification = verified ?? report?.verification ?? null;
  const health = rechecked ?? report?.health ?? null;

  const domain = sender ? senderDomain(sender) : null;
  const publicHost = isPublicMailboxDomain(domain);

  /**
   * The two buttons that "run a check". The wait is deliberate — a check that
   * returns instantly reads as a lie — and it ends by moving the report on the
   * way a real resolver would: a missing record comes back as propagating, one
   * that was propagating comes back correct.
   */
  const runCheck = (kind: 'domain' | 'health') => {
    if (report === null || checking !== null) return;
    setChecking(kind);
    timerRef.current = window.setTimeout(() => {
      if (kind === 'domain') {
        setVerified((current) => advanceDomainVerification(current ?? report.verification));
      } else {
        setRechecked((current) => advanceMailHealth(current ?? report.health));
      }
      setChecking(null);
      timerRef.current = null;
    }, 1400);
  };

  const [tab, setTab] = useState<TabId>('overview');

  const tabs: TabDef[] = [];
  if (sender && verification && health) {
    tabs.push({ id: 'overview', label: 'Overview', icon: BarChart3, count: null });
    // if (!publicHost) {
    //   tabs.push({
    //     id: 'domain',
    //     label: 'Domain',
    //     icon: ShieldCheck,
    //     count: verification.blockers.length > 0 ? verification.blockers.length : null,
    //   });
    // }
  }

  const activeTab = tabs.some((entry) => entry.id === tab) ? tab : 'overview';

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <header className="shrink-0 px-8 pt-7 pb-4">
        <Link
          to="/emails/senders"
          className="text-muted hover:text-ink-soft mb-3 inline-flex items-center gap-1.5 text-[12px] font-semibold transition-colors"
        >
          <ArrowLeft size={13} />
          Senders
        </Link>

        {sender ? (
          <>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="min-w-0">
                <p className="kicker mb-2">
                  <span className="bg-accent h-1.5 w-1.5 rounded-full" />
                  {publicHost ? 'Shared mailbox sender' : 'Domain sender'}
                </p>
                <h1 className="text-ink flex min-w-0 items-center gap-2 text-[22px] font-bold tracking-tight">
                  <span className="truncate">{sender.name}</span>
                </h1>
                <p className="text-ink-soft mt-1 text-[13px]">
                  {sender.senderType === 'smtp' ? 'SMTP' : 'Brevo API'} ·{' '}
                  {senderFromAddress(sender) ?? 'no From address yet'} · via {senderRelay(sender)}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${
                    sender.isActive ? 'bg-ok/10 text-ok' : 'bg-ink/5 text-muted'
                  }`}
                >
                  {sender.isActive ? 'Active' : 'Inactive'}
                </span>

                <Link to="/emails/senders" className="btn-secondary px-3 py-1.5 text-xs">
                  Manage senders
                </Link>
              </div>
            </div>

            <nav
              role="tablist"
              aria-label="Sender views"
              className="border-line bg-bg/60 mt-5 flex max-w-full items-center gap-1 overflow-x-auto rounded-full border p-1"
            >
              {tabs.map(({ id: tabId, label, icon: Icon, count }) => {
                const selected = activeTab === tabId;
                return (
                  <button
                    key={tabId}
                    id={`sender-tab-${tabId}`}
                    type="button"
                    role="tab"
                    aria-selected={selected}
                    aria-controls={`sender-panel-${tabId}`}
                    onClick={() => setTab(tabId)}
                    className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[13px] font-semibold whitespace-nowrap transition-all duration-150 ${
                      selected ? 'bg-card text-ink shadow-sm' : 'text-muted hover:text-ink-soft'
                    }`}
                  >
                    <Icon size={13} strokeWidth={2.2} />
                    {label}
                    {count !== null && (
                      <span
                        className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                          selected ? 'bg-warn/15 text-warn' : 'bg-ink/5 text-muted'
                        }`}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </>
        ) : null}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-8 pb-10">
        {senderQuery.isPending ? (
          <PaneLoading label="Loading sender…" />
        ) : senderQuery.isError ? (
          <div className="flex h-64 flex-col items-center justify-center gap-3 text-center">
            <TriangleAlert size={22} className="text-warn" />
            <p className="text-ink text-sm font-semibold">This sender could not be loaded</p>
            <p className="text-muted max-w-sm text-[12.5px]">
              It may have been deleted, or it belongs to another workspace.
            </p>
            <Link to="/emails/senders" className="btn-secondary mt-1 px-3 py-1.5 text-xs">
              Back to senders
            </Link>
          </div>
        ) : !sender || !verification || !health ? (
          <div className="flex h-64 items-center justify-center">
            <p className="text-muted text-sm">This sender no longer exists.</p>
          </div>
        ) : (
          <div
            role="tabpanel"
            id={`sender-panel-${activeTab}`}
            aria-labelledby={`sender-tab-${activeTab}`}
            className="space-y-4"
          >
            {activeTab === 'overview' && (
              <OverviewTab
                sender={sender}
                metrics={buildSenderMetrics(metrics)}
                verification={verification}
                health={health}
                onOpenTab={setTab}
              />
            )}
            {activeTab === 'domain' && (
              <DomainTab
                sender={sender}
                verification={verification}
                busy={checking === 'domain'}
                onCheck={() => runCheck('domain')}
              />
            )}
            {activeTab === 'health' && (
              <MailHealthTab
                health={health}
                busy={checking === 'health'}
                onCheck={() => runCheck('health')}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Overview
// ─────────────────────────────────────────────────────────────────

function OverviewTab({
  sender,
  metrics,
  verification,
  health,
  onOpenTab,
}: {
  sender: EmailSender;
  metrics: EnrichedSenderMetrics | null;
  verification: DomainVerification;
  health: MailHealth;
  onOpenTab: (tab: TabId) => void;
}) {
  const data = senderData(sender);

  const delta = (current: number, previous: number, goodWhenUp: boolean) => {
    const change = deltaPercent(current, previous);
    if (change === null) return null;
    return { value: change, good: goodWhenUp ? change >= 0 : change <= 0 };
  };

  if (!metrics) return null;

  const { totals, rates } = metrics;

  return (
    <>
      {health.publicHost ? (
        <StatusBanner
          tone={health.fixes.length > 0 ? 'warn' : 'ok'}
          icon={Globe}
          title={`Mail is sent as ${health.domain} — a shared mailbox domain`}
          body={
            health.fixes.length > 0
              ? `${health.provider ?? 'The provider'} owns the DNS zone, so ownership cannot be verified and its records are not yours to change. ${health.fixes.length} finding${health.fixes.length === 1 ? ' is' : 's are'} worth reading anyway.`
              : `Ownership of ${health.domain} cannot be verified — the zone belongs to ${health.provider ?? 'the provider'}. The Mail health tab reports what a receiver sees instead.`
          }
          action={{ label: 'Open mail health', onClick: () => onOpenTab('health') }}
        />
      ) : (
        <StatusBanner
          tone={
            verification.status === 'verified'
              ? 'ok'
              : verification.status === 'pending'
                ? 'warn'
                : 'bad'
          }
          icon={verification.status === 'verified' ? BadgeCheck : ShieldCheck}
          title={
            verification.status === 'verified'
              ? `You manage ${verification.domain}`
              : verification.status === 'pending'
                ? `Verifying ${verification.domain} — DNS is still catching up`
                : `${verification.domain} is not verified yet`
          }
          body={
            verification.status === 'verified'
              ? `Ownership was confirmed${verification.verifiedAt ? ` on ${formatDate(verification.verifiedAt)}` : ''}. Mail from this sender carries a domain you control.`
              : verification.status === 'pending'
                ? 'The ownership record is in place. The remaining records resolve as soon as their TTLs expire.'
                : `${verification.blockers.length} record${verification.blockers.length === 1 ? '' : 's'} must be added before this domain can be verified and used to send.`
          }
          action={{ label: 'Domain & DNS records', onClick: () => onOpenTab('domain') }}
        />
      )}

      {/* ── Metrics ─────────────────────────────────────────── */}
      <section>
        <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
          <h2 className="panel-title">Last {metrics.windowDays} days</h2>
          <p className="text-muted text-[11.5px]">
            Compared with the previous {metrics.windowDays} days, {formatDay(metrics.previousFrom)}{' '}
            – {formatDay(metrics.previousTo)}.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
          <MetricTile
            label="Emails sent"
            value={formatCount(totals.sent)}
            hint={`${totals.sent.toLocaleString()} attempts`}
            tone={null}
            delta={delta(totals.sent, metrics.previous.sent, true)}
          />
          <MetricTile
            label="Delivered"
            value={formatCount(totals.delivered)}
            hint={`${formatCount(totals.bounced)} bounced · ${formatCount(totals.failed)} failed`}
            tone={null}
            delta={delta(totals.delivered, metrics.previous.delivered, true)}
          />
          <MetricTile
            label="Delivery rate"
            value={formatRate(rates.delivery, 1)}
            hint={`providers want ${METRIC_THRESHOLDS.delivery.good}%+`}
            tone={rateTone(
              rates.delivery,
              METRIC_THRESHOLDS.delivery.good,
              METRIC_THRESHOLDS.delivery.watch,
              true,
            )}
            delta={delta(rates.delivery, rateOfTotals(metrics.previous, 'delivery'), true)}
          />
          <MetricTile
            label="Bounce rate"
            value={formatRate(rates.bounce)}
            hint={`keep under ${METRIC_THRESHOLDS.bounce.good}%`}
            tone={rateTone(
              rates.bounce,
              METRIC_THRESHOLDS.bounce.good,
              METRIC_THRESHOLDS.bounce.watch,
            )}
            delta={delta(rates.bounce, rateOfTotals(metrics.previous, 'bounce'), false)}
          />
          <MetricTile
            label="Complaint rate"
            value={formatRate(rates.complaint, 3)}
            hint={`keep under ${METRIC_THRESHOLDS.complaint.good}%`}
            tone={rateTone(
              rates.complaint,
              METRIC_THRESHOLDS.complaint.good,
              METRIC_THRESHOLDS.complaint.watch,
            )}
            delta={delta(rates.complaint, rateOfTotals(metrics.previous, 'complaint'), false)}
          />
          <MetricTile
            label="Open rate"
            value={formatRate(rates.open, 1)}
            hint={`${METRIC_THRESHOLDS.open.good}%+ is healthy`}
            tone={rateTone(
              rates.open,
              METRIC_THRESHOLDS.open.good,
              METRIC_THRESHOLDS.open.watch,
              true,
            )}
            delta={delta(rates.open, rateOfTotals(metrics.previous, 'open'), true)}
          />
          <MetricTile
            label="Click rate"
            value={formatRate(rates.click, 1)}
            hint={`${METRIC_THRESHOLDS.click.good}%+ is healthy`}
            tone={rateTone(
              rates.click,
              METRIC_THRESHOLDS.click.good,
              METRIC_THRESHOLDS.click.watch,
              true,
            )}
            delta={delta(rates.click, rateOfTotals(metrics.previous, 'click'), true)}
          />
        </div>
      </section>

      {/* ── Volume + delivery ───────────────────────────────── */}
      <div className="grid gap-4 lg:grid-cols-[1.6fr_1fr]">
        <section className="surface-row px-4 py-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="panel-title">Volume</h2>
            <p className="text-muted text-[11.5px]">Emails handed to the relay, per day</p>
          </div>
          <VolumeChart daily={metrics.daily} />
        </section>

        <section className="surface-row px-4 py-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="panel-title">Reputation</h2>
            <span
              className={`badge ${
                metrics.reputation.grade === 'A' || metrics.reputation.grade === 'B'
                  ? 'badge-published'
                  : metrics.reputation.grade === 'C'
                    ? 'badge-review'
                    : 'badge-failed'
              }`}
            >
              {metrics.reputation.label}
            </span>
          </div>

          <div className="flex items-end gap-3">
            <p className="stat-value">{metrics.reputation.score}</p>
            <p className="text-muted pb-1 text-[11.5px]">
              / 100 · grade {metrics.reputation.grade}
            </p>
          </div>
          <div className="bg-ink/5 mt-2 h-1.5 overflow-hidden rounded-full" aria-hidden="true">
            <div
              className={
                metrics.reputation.score >= 90
                  ? 'bg-ok h-full'
                  : metrics.reputation.score >= 75
                    ? 'bg-warn h-full'
                    : 'bg-danger h-full'
              }
              style={{ width: `${metrics.reputation.score}%` }}
            />
          </div>
          <p className="text-ink-soft mt-3 text-[12px]">{metrics.reputation.note}</p>

          <div className="mt-4">
            <h3 className="field-label">Delivery</h3>
            <DeliverySplit totals={totals} />
          </div>
        </section>
      </div>

      {/* ── Recent campaigns ────────────────────────────────── */}
      {/* <section className="surface-row overflow-hidden">
        <div className="border-line flex items-center justify-between gap-3 border-b px-4 py-3">
          <h2 className="panel-title">Recent campaigns</h2>
          <p className="text-muted text-[11.5px]">Sent through this sender</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[12.5px]">
            <thead>
              <tr className="text-muted border-line border-b text-[10.5px] tracking-wide uppercase">
                <th className="px-4 py-2 font-semibold">Campaign</th>
                <th className="px-3 py-2 font-semibold">Audience</th>
                <th className="px-3 py-2 text-right font-semibold">Sent</th>
                <th className="px-3 py-2 text-right font-semibold">Delivered %</th>
                <th className="px-3 py-2 text-right font-semibold">Opens %</th>
                <th className="px-3 py-2 text-right font-semibold">Clicks %</th>
                <th className="px-3 py-2 text-right font-semibold">Bounces</th>
                <th className="px-4 py-2 text-right font-semibold">When</th>
              </tr>
            </thead>
            <tbody>
              {metrics.campaigns.map((campaign) => (
                <tr key={campaign.id} className="border-line/70 border-b last:border-0">
                  <td className="text-ink max-w-56 truncate px-4 py-2.5 font-medium">
                    {campaign.subject}
                  </td>
                  <td className="text-ink-soft px-3 py-2.5">{campaign.audience}</td>
                  <td className="text-ink px-3 py-2.5 text-right font-mono">
                    {campaign.sent.toLocaleString()}
                  </td>
                  <td className="text-ink-soft px-3 py-2.5 text-right font-mono">
                    {formatRate(rateOf(campaign.delivered, campaign.sent), 1)}
                  </td>
                  <td className="text-ink-soft px-3 py-2.5 text-right font-mono">
                    {formatRate(rateOf(campaign.opens, campaign.delivered), 1)}
                  </td>
                  <td className="text-ink-soft px-3 py-2.5 text-right font-mono">
                    {formatRate(rateOf(campaign.clicks, campaign.delivered), 1)}
                  </td>
                  <td
                    className={`px-3 py-2.5 text-right font-mono ${
                      rateOf(campaign.bounced, campaign.sent) > METRIC_THRESHOLDS.bounce.good
                        ? 'text-danger'
                        : 'text-ink-soft'
                    }`}
                  >
                    {campaign.bounced.toLocaleString()}
                  </td>
                  <td className="text-muted px-4 py-2.5 text-right whitespace-nowrap">
                    {formatDay(campaign.sentAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section> */}

      {/* ── The delivered identity (real data) ──────────────── */}
      <section className="surface-row px-4 py-4">
        <h2 className="panel-title mb-3">Delivery identity</h2>
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
          <DetailRow label="Type" value={sender.senderType === 'smtp' ? 'SMTP' : 'Brevo API'} />
          <DetailRow label="Relay" value={senderRelay(sender)} mono />
          <DetailRow
            label="Transport security"
            value={
              sender.senderType === 'smtp'
                ? data.secure === true
                  ? 'Implicit TLS (port 465 style)'
                  : 'STARTTLS on connect'
                : 'HTTPS to the Brevo API'
            }
          />
          <DetailRow label="From address" value={senderFromAddress(sender) ?? '—'} mono />
          <DetailRow
            label="From name"
            value={String(data.fromName ?? (sender.senderType === 'smtp' ? '—' : '')) || '—'}
          />
          <DetailRow
            label="Credentials"
            value={sender.hasSecret ? 'Stored (encrypted)' : 'Missing'}
            tone={sender.hasSecret ? 'ok' : 'bad'}
          />
          <DetailRow
            label="Last used"
            value={sender.lastUsedAt ? formatTime(sender.lastUsedAt) : 'Never used'}
          />
          <DetailRow label="Created" value={formatTime(sender.createdAt)} />
          <DetailRow label="Sender id" value={sender.id} mono />
        </dl>
        {sender.description && (
          <p className="text-ink-soft border-line mt-3 border-t pt-3 text-[12.5px]">
            {sender.description}
          </p>
        )}
      </section>
    </>
  );
}

/** The previous window as rates, so a tile can compare like with like. */
function rateOfTotals(
  totals: MetricTotals,
  metric: 'delivery' | 'bounce' | 'complaint' | 'open' | 'click',
): number {
  switch (metric) {
    case 'delivery':
      return rateOf(totals.delivered, totals.sent);
    case 'bounce':
      return rateOf(totals.bounced, totals.sent);
    case 'complaint':
      return rateOf(totals.complained, totals.delivered);
    case 'open':
      return rateOf(totals.opens, totals.delivered);
    case 'click':
      return rateOf(totals.clicks, totals.delivered);
  }
}

// ─────────────────────────────────────────────────────────────────
// Domain tab — "prove you manage this domain", then the records
// ─────────────────────────────────────────────────────────────────

function DomainTab({
  sender,
  verification,
  busy,
  onCheck,
}: {
  sender: EmailSender;
  verification: DomainVerification;
  busy: boolean;
  onCheck: () => void;
}) {
  const [onlyProblems, setOnlyProblems] = useState(true);
  const pending = recordsToAdd(verification.checks);
  const from = senderFromAddress(sender);
  const visible = onlyProblems
    ? verification.checks.filter((check) => check.status !== 'ok' && check.status !== 'optional')
    : verification.checks;

  const statusTone =
    verification.status === 'verified'
      ? { className: 'badge-published', label: 'Verified' }
      : verification.status === 'pending'
        ? { className: 'badge-sending', label: 'Propagating' }
        : { className: 'badge-failed', label: 'Action needed' };

  return (
    <>
      {/* ── Ownership ───────────────────────────────────────── */}
      <section className="surface-row px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="panel-title">Domain ownership</h2>
              <span className={`badge ${statusTone.className}`}>{statusTone.label}</span>
            </div>
            <p className="text-ink-soft mt-1.5 max-w-3xl text-[12.5px]">
              {verification.status === 'verified' ? (
                <>
                  BlogForger read the TXT record below from {verification.domain} and matched it to
                  this workspace
                  {verification.verifiedAt ? ` on ${formatDate(verification.verifiedAt)}` : ''}.
                  Mail from this sender is signed as a domain you control.
                </>
              ) : (
                <>
                  One TXT record is all it takes to prove you manage{' '}
                  <span className="text-ink font-medium">{verification.domain}</span> — and it is
                  what lets BlogForger sign your mail as that domain instead of sending on a relay’s
                  reputation.
                </>
              )}
            </p>
          </div>

          <button
            type="button"
            onClick={onCheck}
            disabled={busy}
            className="btn-secondary shrink-0 px-3 py-1.5 text-xs disabled:opacity-50"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            {busy ? 'Checking DNS…' : 'Check verification'}
          </button>
        </div>

        <div className="mt-4">
          <RecordRow
            record={{
              type: 'TXT',
              host: '_blogforger',
              fqdn: verification.ownershipHost,
              value: `blogforger-verify=${verification.token}`,
              ttl: 'Auto',
            }}
            note="Only someone who controls the zone can publish this value."
          />
        </div>

        {verification.blockers.length > 0 && (
          <div className="border-warn/25 bg-warn/8 mt-4 rounded-xl border px-3.5 py-3">
            <p className="text-warn flex items-center gap-1.5 text-[12px] font-semibold">
              <TriangleAlert size={13} />
              {verification.blockers.length} record
              {verification.blockers.length === 1 ? '' : 's'} still blocking verification
            </p>
            <ul className="mt-1.5 space-y-1">
              {verification.blockers.map((check) => (
                <li key={check.id} className="text-ink-soft text-[12px]">
                  {check.label}
                  {check.expected ? (
                    <span className="text-muted font-mono"> — {check.expected.fqdn}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* ── Records ─────────────────────────────────────────── */}
      <section>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="panel-title">Mail records</h2>
            <p className="text-muted mt-0.5 text-[11.5px]">
              Read from public DNS for {from ?? verification.domain}.{' '}
              {pending.length > 0
                ? `${pending.length} record${pending.length === 1 ? '' : 's'} to add or fix.`
                : 'Nothing to add right now.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {pending.length > 0 && (
              <CopyValue
                value={pendingRecordsText(verification.checks)}
                label="Copy all"
                withIcon
                variant="button"
              />
            )}
            <label className="border-line bg-bg/60 text-ink-soft flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-1.5 text-[11.5px] font-semibold">
              <input
                type="checkbox"
                checked={onlyProblems}
                onChange={(event) => setOnlyProblems(event.target.checked)}
                className="border-line-strong rounded"
              />
              <ListFilter size={12} />
              Only what needs work
            </label>
          </div>
        </div>

        {pending.length > 0 && (
          <RecordsDigest
            records={pending}
            domain={verification.domain}
            hint="Add these in your DNS provider, then press “Check verification”."
          />
        )}

        <div className="space-y-2.5">
          {visible.length === 0 ? (
            <div className="surface-row px-4 py-6 text-center">
              <CheckCircle2 size={18} className="text-ok mx-auto" />
              <p className="text-ink mt-2 text-[13px] font-semibold">
                Every record we check is right
              </p>
              <p className="text-muted mt-0.5 text-[12px]">
                Untick “Only what needs work” to see the records that are already answering.
              </p>
            </div>
          ) : (
            visible.map((check) => <DnsCheckCard key={check.id} check={check} />)
          )}
        </div>

        <p className="text-muted mt-3 text-[11px]">
          The lookups, answers and check times on this tab are sample data: the resolver behind them
          is not built yet, so the only thing that moves a status here is “Check verification”.
        </p>
      </section>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────
// Mail health tab — the shared-mailbox answer
// ─────────────────────────────────────────────────────────────────

function MailHealthTab({
  health,
  busy,
  onCheck,
}: {
  health: MailHealth;
  busy: boolean;
  onCheck: () => void;
}) {
  const [onlyProblems, setOnlyProblems] = useState(true);
  const visible = onlyProblems
    ? health.checks.filter((check) => check.status !== 'ok' && check.status !== 'optional')
    : health.checks;

  return (
    <>
      {/* ── Report header ───────────────────────────────────── */}
      <section className="surface-row px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="kicker mb-1.5">
              <Gauge size={13} />
              Mail health report
            </p>
            <h2 className="text-ink flex flex-wrap items-center gap-2 text-[17px] font-bold tracking-tight">
              {health.domain}
              <span
                className={`badge ${
                  health.grade === 'A' || health.grade === 'B'
                    ? 'badge-published'
                    : health.grade === 'C'
                      ? 'badge-review'
                      : 'badge-failed'
                }`}
              >
                Grade {health.grade}
              </span>
            </h2>
            <p className="text-ink-soft mt-1 max-w-3xl text-[12.5px]">{health.verdict}</p>
            <p className="text-muted mt-2 text-[11.5px]">
              Last checked {formatTime(health.checkedAt)} · relay {health.relay} · resolvers{' '}
              {health.resolvers.join(', ')}
            </p>
          </div>

          <div className="shrink-0 text-right">
            <p className="stat-value">{health.score}</p>
            <p className="text-muted text-[11px]">out of 100</p>
            <button
              type="button"
              onClick={onCheck}
              disabled={busy}
              className="btn-secondary mt-2 px-3 py-1.5 text-xs disabled:opacity-50"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
              {busy ? 'Re-checking…' : 'Re-check DNS'}
            </button>
          </div>
        </div>
      </section>

      {/* ── Why this tab, and not a domain check ────────────── */}
      <section className="border-line bg-bg/60 rounded-2xl border px-4 py-3.5">
        <p className="text-ink flex items-center gap-1.5 text-[12.5px] font-semibold">
          <Lock size={13} className="text-muted" />
          Ownership of {health.domain} cannot be verified
        </p>
        <p className="text-ink-soft mt-1 text-[12px]">
          It is a shared mailbox domain, so the DNS zone belongs to{' '}
          {health.provider ?? 'the mail provider'} — nobody but them can add a record to it. Your
          sender still works, and BlogForger still signs what it sends, but the domain’s SPF, DKIM
          and DMARC decisions are theirs. That is why this tab reports mail health instead of asking
          you for records.
        </p>
        <p className="text-muted mt-2 text-[11.5px]">
          To get a verified domain, a signed reputation of your own and full control of these
          records, add a sender on a domain you own.
        </p>
      </section>

      {/* ── The findings ────────────────────────────────────── */}
      <section>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="panel-title">What public DNS says</h2>
            <p className="text-muted mt-0.5 text-[11.5px]">
              {health.fixes.length === 0
                ? 'Nothing to change — every check passes.'
                : `${health.fixes.length} finding${health.fixes.length === 1 ? '' : 's'}, with what to do about each.`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* No "copy the records" button here on purpose: a shared mailbox
                has no record the user can publish, only steps to follow. */}
            <label className="border-line bg-bg/60 text-ink-soft flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-1.5 text-[11.5px] font-semibold">
              <input
                type="checkbox"
                checked={onlyProblems}
                onChange={(event) => setOnlyProblems(event.target.checked)}
                className="border-line-strong rounded"
              />
              <ListFilter size={12} />
              Only what needs work
            </label>
          </div>
        </div>

        <div className="space-y-2.5">
          {visible.length === 0 ? (
            <div className="surface-row px-4 py-6 text-center">
              <CheckCircle2 size={18} className="text-ok mx-auto" />
              <p className="text-ink mt-2 text-[13px] font-semibold">Health checks all pass</p>
              <p className="text-muted mt-0.5 text-[12px]">
                Untick “Only what needs work” to read the individual records.
              </p>
            </div>
          ) : (
            visible.map((check) => <DnsCheckCard key={check.id} check={check} />)
          )}
        </div>

        <p className="text-muted mt-3 text-[11px]">
          Sample data: the resolver behind this report is not built yet, so “Re-check DNS” moves the
          statuses forward locally rather than querying anything.
        </p>
      </section>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────
// Pieces
// ─────────────────────────────────────────────────────────────────

function StatusBanner({
  tone,
  icon: Icon,
  title,
  body,
  action,
}: {
  tone: 'ok' | 'warn' | 'bad';
  icon: LucideIcon;
  title: string;
  body: string;
  action: { label: string; onClick: () => void };
}) {
  const skin =
    tone === 'ok'
      ? 'border-ok/25 bg-ok/8 text-ok'
      : tone === 'warn'
        ? 'border-warn/25 bg-warn/8 text-warn'
        : 'border-danger/25 bg-danger/8 text-danger';

  return (
    <section className={`flex flex-wrap items-start gap-3 rounded-2xl border px-4 py-3.5 ${skin}`}>
      <Icon size={17} className="mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-ink text-[13px] font-semibold">{title}</p>
        <p className="text-ink-soft mt-0.5 text-[12px]">{body}</p>
      </div>
      <button
        type="button"
        onClick={action.onClick}
        className="btn-secondary shrink-0 px-3 py-1.5 text-xs"
      >
        {action.label}
      </button>
    </section>
  );
}

export function MetricTile({
  label,
  value,
  hint,
  tone,
  delta,
}: {
  label: string;
  value: string;
  hint: string;
  tone: MetricTone | null;
  delta: { value: number; good: boolean } | null;
}) {
  return (
    <div className="surface-row px-3.5 py-3">
      <p className="text-muted text-[10.5px] font-semibold tracking-wide uppercase">{label}</p>
      <div className="mt-1 flex items-baseline gap-2">
        <p
          className={`text-[19px] leading-none font-bold tracking-tight ${tone ? TONE_TEXT[tone] : 'text-ink'}`}
        >
          {value}
        </p>
        {delta && <DeltaChip value={delta.value} good={delta.good} />}
      </div>
      <p className="text-muted mt-1.5 text-[11px]">{hint}</p>
    </div>
  );
}

function DeltaChip({ value, good }: { value: number; good: boolean }) {
  const rounded = Math.abs(value) < 0.05 ? 0 : value;
  const Icon = rounded === 0 ? Minus : rounded > 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${
        rounded === 0 ? 'text-muted' : good ? 'text-ok' : 'text-danger'
      }`}
      title="Against the previous period"
    >
      <Icon size={11} />
      {rounded === 0 ? 'flat' : `${Math.abs(rounded).toFixed(1)}%`}
    </span>
  );
}

/** The 30-day volume bars. Pure CSS — no chart library, no canvas. */
export function VolumeChart({ daily }: { daily: readonly DailyVolume[] }) {
  if (daily.length === 0) {
    return (
      <p className="text-muted py-8 text-center text-[12.5px]">No volume in this window yet.</p>
    );
  }

  const total = daily.reduce((sum, day) => sum + day.sent, 0);
  const peak = daily.reduce(
    (best, day) => (day.sent > best.sent ? day : best),
    daily[0] as DailyVolume,
  );
  const max = Math.max(1, ...daily.map((day) => day.sent));
  const first = daily[0] as DailyVolume;
  const last = daily[daily.length - 1] as DailyVolume;

  return (
    <figure className="m-0">
      <div
        className="flex h-28 items-end gap-[3px]"
        role="img"
        aria-label={`Emails sent per day over the last ${daily.length} days: ${total.toLocaleString()} in total, peaking at ${peak.sent.toLocaleString()} on ${formatDay(peak.date)}.`}
      >
        {daily.map((day) => (
          <div
            key={day.date}
            className="bg-accent/30 hover:bg-accent min-w-[3px] flex-1 rounded-t-[3px] transition-colors"
            style={{ height: `${Math.max(3, (day.sent / max) * 100)}%` }}
            title={`${formatDay(day.date)} — ${day.sent.toLocaleString()} sent, ${day.delivered.toLocaleString()} delivered`}
          />
        ))}
      </div>
      <figcaption className="text-muted mt-2 flex items-center justify-between text-[11px]">
        <span>{formatDay(first.date)}</span>
        <span>
          {total.toLocaleString()} sent · peak {peak.sent.toLocaleString()} on{' '}
          {formatDay(peak.date)}
        </span>
        <span>{formatDay(last.date)}</span>
      </figcaption>
    </figure>
  );
}

/** Delivered / bounced / complained / failed, as tallies plus one bar. */
export function DeliverySplit({ totals }: { totals: MetricTotals }) {
  const tiles: { label: string; value: number; tone: string }[] = [
    { label: 'Delivered', value: totals.delivered, tone: 'text-ok' },
    { label: 'Bounced', value: totals.bounced, tone: 'text-danger' },
    { label: 'Complained', value: totals.complained, tone: 'text-danger' },
    { label: 'Failed', value: totals.failed, tone: 'text-ink-soft' },
  ];

  return (
    <div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {tiles.map((tile) => (
          <div key={tile.label} className="border-line bg-bg/60 rounded-xl border px-2.5 py-2">
            <p className={`text-[15px] leading-none font-bold tracking-tight ${tile.tone}`}>
              {tile.value.toLocaleString()}
            </p>
            <p className="text-muted mt-1 text-[10px] tracking-wide uppercase">{tile.label}</p>
          </div>
        ))}
      </div>
      {totals.sent > 0 && (
        <div className="bg-ink/5 mt-2.5 flex h-1.5 overflow-hidden rounded-full" aria-hidden="true">
          <div
            className="bg-ok h-full"
            style={{ width: `${(totals.delivered / totals.sent) * 100}%` }}
          />
          <div
            className="bg-danger h-full"
            style={{
              width: `${((totals.bounced + totals.complained + totals.failed) / totals.sent) * 100}%`,
            }}
          />
        </div>
      )}
    </div>
  );
}

const CHECK_STATUS: Record<
  DnsCheckStatus,
  { label: string; badge: string; icon: LucideIcon; text: string }
> = {
  ok: { label: 'Correct', badge: 'badge-published', icon: CheckCircle2, text: 'text-ok' },
  propagating: { label: 'Propagating', badge: 'badge-sending', icon: Clock, text: 'text-accent' },
  warn: { label: 'Worth fixing', badge: 'badge-review', icon: TriangleAlert, text: 'text-warn' },
  action: { label: 'Action needed', badge: 'badge-failed', icon: XCircle, text: 'text-danger' },
  optional: { label: 'Optional', badge: 'badge-draft', icon: Info, text: 'text-muted' },
};

/** One DNS finding, plus exactly what to do about it. */
export function DnsCheckCard({ check }: { check: DnsCheck }) {
  const meta = CHECK_STATUS[check.status];
  const Icon = meta.icon;

  return (
    <article
      className={`surface-row px-4 py-3.5 ${check.status === 'action' ? 'border-danger/25' : ''}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Icon size={14} className={`${meta.text} shrink-0`} />
        <h3 className="text-ink text-[13.5px] font-semibold">{check.label}</h3>
        <span className={`badge ${meta.badge}`}>{meta.label}</span>
        {/* "Not required" is reassurance next to a warning, but it would
            contradict an "Action needed" badge — there the fix speaks for
            itself. */}
        {!check.required && check.status !== 'action' && (
          <span className="text-muted text-[10.5px] font-semibold tracking-wide uppercase">
            not required
          </span>
        )}
      </div>
      <p className="text-muted mt-1 text-[11.5px]">{check.question}</p>

      <dl className="mt-2.5 grid gap-x-6 gap-y-2 lg:grid-cols-3">
        <div className="min-w-0">
          <dt className="text-muted text-[10.5px] tracking-wide uppercase">Looked up</dt>
          <dd className="text-ink-soft truncate font-mono text-[11.5px]" title={check.lookup.name}>
            {check.lookup.type} {check.lookup.host}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="text-muted text-[10.5px] tracking-wide uppercase">Found</dt>
          <dd
            className={`truncate font-mono text-[11.5px] ${check.found ? 'text-ink' : 'text-muted'}`}
            title={check.found ?? 'nothing'}
          >
            {check.found ?? 'Nothing published'}
          </dd>
        </div>
        {check.expected && (
          <div className="min-w-0">
            <dt className="text-muted text-[10.5px] tracking-wide uppercase">
              {check.status === 'action' ? 'Add this' : 'Should be'}
            </dt>
            <dd className="flex items-start gap-1.5">
              <span
                className="text-ink-soft min-w-0 flex-1 font-mono text-[11.5px] break-all"
                title={check.expected.value}
              >
                {check.expected.type} {check.expected.host} → {check.expected.value}
              </span>
              <CopyValue value={check.expected.value} />
            </dd>
          </div>
        )}
      </dl>

      <p className="text-ink-soft mt-2 text-[12px]">{check.why}</p>

      {check.fix && (
        <div className="border-line bg-bg/60 mt-3 rounded-xl border px-3.5 py-3">
          <p className="text-ink flex items-center gap-1.5 text-[12.5px] font-semibold">
            <Sparkles size={12} className="text-accent" />
            {check.fix.summary}
          </p>

          {check.fix.alternative && (
            <p className="text-ink-soft mt-1.5 text-[12px]">{check.fix.alternative}</p>
          )}

          <ol className="mt-2 space-y-1.5">
            {check.fix.steps.map((step, index) => (
              <li key={step} className="text-ink-soft flex gap-2 text-[12px]">
                <span className="bg-ink/5 text-muted mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] font-bold">
                  {index + 1}
                </span>
                <span className="min-w-0">{step}</span>
              </li>
            ))}
          </ol>

          {check.expected && (
            <div className="mt-2.5">
              <RecordRow
                record={check.expected}
                note={
                  check.expected.type === 'MX'
                    ? 'Priority first, then the mail server.'
                    : check.expected.type === 'CNAME'
                      ? 'The trailing dot is part of the target.'
                      : 'Paste the whole value, quotes included.'
                }
              />
            </div>
          )}

          {check.fix.extra && (
            <div className="mt-2">
              <RecordRow record={check.fix.extra} note="Companion record for reporting." />
            </div>
          )}
        </div>
      )}
    </article>
  );
}

/** A DNS record as a panel asks for it: type, name, value, TTL, copy. */
export function RecordRow({ record, note }: { record: DnsRecord; note: string | null }) {
  return (
    <div className="border-line bg-card rounded-xl border px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="bg-ink/5 text-ink-soft rounded-md px-1.5 py-0.5 font-mono text-[10.5px] font-bold">
          {record.type}
        </span>
        <span className="text-ink font-mono text-[12px] break-all">{record.fqdn}</span>
        <span className="text-muted text-[10.5px]">TTL {record.ttl}</span>
        <span className="ml-auto">
          <CopyValue value={record.value} label="Copy value" withIcon variant="button" />
        </span>
      </div>
      <p className="text-ink-soft mt-1.5 font-mono text-[11.5px] break-all">{record.value}</p>
      {note && <p className="text-muted mt-1 text-[11px]">{note}</p>}
    </div>
  );
}

/** Everything the user must add, in one place, as one copyable block. */
export function RecordsDigest({
  records,
  domain,
  hint,
}: {
  records: readonly DnsRecord[];
  domain: string;
  hint: string;
}) {
  return (
    <div className="border-accent-border bg-accent/5 mb-2.5 rounded-xl border px-3.5 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-ink flex items-center gap-1.5 text-[12.5px] font-semibold">
          <Sparkles size={12} className="text-accent" />
          {records.length} record{records.length === 1 ? '' : 's'} to add at your DNS provider
        </p>
        <CopyValue value={recordsText(records)} label="Copy all" withIcon variant="button" />
      </div>
      <ul className="mt-2 space-y-1">
        {records.map((record) => (
          <li
            key={`${record.type}-${record.fqdn}`}
            className="text-ink-soft font-mono text-[11.5px] break-all"
          >
            {record.type} <span className="text-muted">{record.host}</span> → {record.value}
          </li>
        ))}
      </ul>
      <p className="text-muted mt-2 text-[11px]">
        {hint} Zone: <span className="font-mono">{domain}</span>
      </p>
    </div>
  );
}

export function CopyValue({
  value,
  label,
  withIcon = false,
  variant = 'icon',
}: {
  value: string;
  label?: string;
  withIcon?: boolean;
  variant?: 'icon' | 'button';
}) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    const clipboard = navigator.clipboard;
    if (!clipboard) return;
    void clipboard.writeText(value).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1600);
      },
      () => undefined,
    );
  };

  if (variant === 'icon') {
    return (
      <button
        type="button"
        onClick={copy}
        title={copied ? 'Copied' : `Copy ${label ?? value}`}
        aria-label={copied ? 'Copied' : `Copy ${label ?? value}`}
        className="text-muted hover:text-ink shrink-0 rounded p-1 transition-colors"
      >
        {copied ? <Check size={12} className="text-ok" /> : <Copy size={12} />}
      </button>
    );
  }

  return (
    <button type="button" onClick={copy} className="btn-secondary px-2.5 py-1.5 text-[11.5px]">
      {copied ? <Check size={12} className="text-ok" /> : withIcon ? <Copy size={12} /> : null}
      {copied ? 'Copied' : (label ?? 'Copy')}
    </button>
  );
}

function DetailRow({
  label,
  value,
  mono = false,
  tone,
}: {
  label: string;
  value: string;
  mono?: boolean;
  tone?: 'ok' | 'bad';
}) {
  return (
    <div className="min-w-0">
      <dt className="text-muted text-[10.5px] tracking-wide uppercase">{label}</dt>
      <dd
        className={`truncate text-[12.5px] ${mono ? 'font-mono' : ''} ${
          tone === 'ok' ? 'text-ok' : tone === 'bad' ? 'text-danger' : 'text-ink-soft'
        }`}
        title={value}
      >
        {value}
      </dd>
    </div>
  );
}

// Icons are imported above only for the shapes the page renders.
